import { feathers } from '@/feathers-client';
import { transformCondition, addToQueryObject } from '@/Contacts/store/contact-filters';
import { HIDE_ERROR_AFTER_MS } from '@/Core/utilities/index';
import { generateSlippingAwayConditions } from '@/Contacts/utilities';
import { ESSearchHeader } from '@/Core/config/es-search-header';

function addToQuery(queries, groupConditionType, subquery) {
  // Handle the '$or' group condition
  if (groupConditionType === '$or') {
    queries.$and.forEach(query => handleOrCondition(query, subquery));
    return;
  }
  // Handle the '$and' group condition when '$or' is present
  else if (groupConditionType === '$and' && queries.$and.some(query => query.$or)) {
    queries.$and.forEach(query => handleAndConditionWithOr(query, subquery));
    return;
  }
  queries.$and.forEach(query => handleAndCondition(query, subquery));
  return
}

function handleOrCondition(query, subquery) {
  if (query.$or) {
    query.$or.push(subquery);
    return;
  } else if (query.$and) {
    transformAndToOr(query, subquery);
    return;
  }
  query.$or = [subquery];
  return;
}

function handleAndConditionWithOr(query, subquery) {
  if (!query.$or.some(cond => cond.$and)) {
    query.$or.push({ $and: [subquery] });
    return
  }
  query.$or.find(cond => cond.$and).$and.push(subquery);
  return;
}

function handleAndCondition(query, subquery) {
  if (query.$and) {
    query.$and.push(subquery);
    return;
  }
  query.$and = [subquery];
  return;
}

function transformAndToOr(query, subquery) {
  query.$or = [{ $and: [...query.$and] }, subquery];
  delete query.$and;
}

const defaultState = {
  dashboardContactFilters: [],
  inProgress: false,
  inProgressCount: 0,
  error: null,
  contactFilterCounts: [],
};

export default {
  namespaced: true,
  state: {
    ...defaultState,
  },
  mutations: {
    setDashboardContactFilters(state, dashboardContactFilters) {
      state.dashboardContactFilters = dashboardContactFilters;
    },
    setContactFilterCounts(state, contactFilterCounts) {
      state.contactFilterCounts = contactFilterCounts;
    },
    appendContactFilterCount(state, contactFilterCount) {
      state.contactFilterCounts.push(contactFilterCount);
    },
    contactFilterRequestInProgress(state, status) {
      state.inProgress = status;
    },
    setInProgressCount(state, count) {
      state.inProgressCount = count;
    },
    decreaseInProgressCount(state) {
      if (state.inProgressCount > 0) {
        state.inProgressCount = state.inProgressCount - 1;
      }
      if (state.inProgressCount === 0) {
        state.inProgress = false;
      }
    },
    contactFiltersError(state, error) {
      state.error = error;
    },
    hideContactFiltersError(state) {
      state.error = null;
    },
  },
  actions: {
    async getDashboardContactFilters({ commit, dispatch }) {
      commit('contactFilterRequestInProgress', true);
      let response;
      try {
        response = await feathers
          .service('contact-filters')
          .find({ query: { display_on_dashboard: true, $limit: 10 } });
        commit('setDashboardContactFilters', response.data);
        await dispatch('getDashboardContactFilterTotals');
      } catch (error) {
        commit('contactFilterRequestInProgress', false);
        return commit('contactFiltersError', error);
      }
    },
    async getDashboardContactFilterTotals({ commit, state,  rootGetters }) {
      commit('setContactFilterCounts', []);
      if (state.dashboardContactFilters.length === 0) {
        return commit('contactFilterRequestInProgress', false);
      }
      commit('setInProgressCount', state.dashboardContactFilters.length);
      state.dashboardContactFilters.forEach(async contactFilter => {
        let query = {};
        try {
          const parsedFilters = JSON.parse(contactFilter.conditions)
          if (!Array.isArray(parsedFilters[0] /* old filter */)) {
            parsedFilters.forEach(condition => {
              const transformedCondition = transformCondition(condition);
              addToQueryObject(transformedCondition, query);
            });
          } else {
            const subqueries = parsedFilters.map(groupCondition => {
              let queryGroup = {};
      
              groupCondition.forEach((condition, index) => {
                const transformedCondition = transformCondition(condition);
                queryGroup = addToQueryObject(transformedCondition, queryGroup, transformedCondition.conditionType, index);
              });
      
              return { groupConditionType: groupCondition[0].groupConditionType, ...queryGroup };
            });
            subqueries.forEach((subquery, index) => {
              const groupConditionType = `$${subquery.groupConditionType || 'and'}`.toLowerCase();
              delete subquery.groupConditionType;
            
              if (index === 0) {
                query.$and = [{ ...subquery }];
              } else {
                if (index === 1) {
                  query.$and = [{ [groupConditionType]: [query.$and[0], subquery] }];
                } else {
                  addToQuery(query, groupConditionType, subquery);
                }
              }
            });
          }
          delete query.search;
          query.$limit = 0;
          const totalResult = await feathers.service('cached-contacts/search').create(
            { query },
            { headers: ESSearchHeader },
          );
          let result = {
            id: contactFilter.id,
            conditions: contactFilter.conditions,
            name: contactFilter.name,
            displayOnDashboard: contactFilter.display_on_dashboard,
            total: totalResult.total,
          };
          if (contactFilter.show_contacts_slipping_away) {
            const slippingAwayResult = await feathers.service('cached-contacts/search').create({
              query: {
                ...query,
                $and: [
                  ...(query.$and || []),
                  ...generateSlippingAwayConditions(contactFilter.slipping_away_days),
                ],
              },
            }, { headers: ESSearchHeader });
            result.slippingAway = true;
            result.slippingAwayTotal = slippingAwayResult.total;
            result.slippingAwayDays = contactFilter.slipping_away_days;
          } else {
            result.slippingAway = false;
            result.slippingAwayTotal = null;
          }
          commit('decreaseInProgressCount');
          if (result !== undefined && typeof result === 'object' && result.hasOwnProperty('id')) {
            commit('appendContactFilterCount', result);
          }

        } catch (error) {
          commit('decreaseInProgressCount');
          commit('contactFiltersError', error);
          setTimeout(() => {
            commit('hideContactFiltersError');
          }, HIDE_ERROR_AFTER_MS);
        }
      });
    },
  },
};
